import { datadogRum } from '@datadog/browser-rum';
import $ from 'jquery';
import Moment from 'moment-timezone';
import { marked } from 'marked';
import 'bootstrap';
import 'svgxuse/svgxuse';
import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/browser';
import get from 'lodash/get';
import moment from 'moment';

import {
  enrichUnhandledExceptionData,
  sentryBeforeSendProcessing,
} from '~/helpers/exceptionHandler';
import { flashMessages } from '~/helpers/messages';
import { ajaxFetch, convertToCamel } from '~/helpers/api';
import { routes, getRoute } from '~/helpers/routes';
import SessionSecurity from '~/helpers/sessions';
import { initializeI18n } from '~/helpers/i18n';
import {
  boot as bootIntercom,
  configure as configureIntercom,
  isIntercomAvailable,
  setAuthTokens,
} from '@usealloy/intercom';
import { loadPublicSupportWidget, openSupport } from '~/helpers/supportWidget';
import { kebabToCaps } from '~/helpers/formatting';

/**
 * Only observe prod.
 *
 * Matches prod rather than listing non-prod because cells are added regularly (`connect-qa-ue1-01`,
 * `ephemeral-ue1-01`), so this is cleaner.
 * dark-prod creates shadow traffic, which we also don't need.
 */
const isProductionCell = environment => /(^|-)prod(-|$)/.test(environment) && !/^dark-prod(-|$)/.test(environment);
// RUM config held from page boot until user context says whether this agent opted in
let deferredNonProdRumConfig = null;
let hasStartedNonProdRum = false;

export const initializeObservabilitySuite = ({
  environment,
  sentryDsn,
  datadogRumAppId,
  datadogRumClientToken,
  forceRum = false,
}) => {
  let releaseVersion = '';

  if (!forceRum && !isProductionCell(environment)) {
    // this function gets called twice - once at env state load and another
    // time at user data load. Save this data for the user load if needed.
    deferredNonProdRumConfig = { datadogRumAppId, datadogRumClientToken };
  }

  if (isProductionCell(environment) || forceRum) {
    if (process.env.APP_VERSION) {
      releaseVersion = `dashboard-frontend@${process.env.APP_VERSION}`;
    }

    datadogRum.init({
      applicationId: datadogRumAppId,
      clientToken: datadogRumClientToken,
      site: 'datadoghq.com',
      service: 'daffy',
      env: environment,
      version: releaseVersion,
      sessionSampleRate: 100,
      // this is the playable video replay, vs just metrics above
      sessionReplaySampleRate: 5,
      trackResources: true,
      trackUserInteractions: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask',
      // DD scopes its session cookie to the exact host, which means if an agent
      // switches between customers on different subdomains, DD will start a new session.
      // Both are subdomains of alloy.com, so this setting tells DD to combine the sessions.
      // Can't span alloy.co <-> alloy.com -- a cookie won't cross registrable domains.
      trackSessionAcrossSubdomains: true,
      allowedTracingUrls: [
        /^https:\/\/(?!www\.)([a-z0-9-]+\.)*alloy\.(co|com)(?::\d+)?(?:[/?#]|$)/,
      ], // Exclude `www.` CMS subdomain to avoid CORS issues with DD headers
      // Defaults to true as of browser-rum v7. The `baggage` header isn't in any of our
      // services' Access-Control-Allow-Headers, so turning off for now.
      propagateTraceBaggage: false,
      beforeSend: event => {
        // Benign browser noise, already ignored by Sentry below
        if (
          event.type === 'error'
          && event.error?.message?.includes('ResizeObserver loop')
        ) {
          return false;
        }

        // Custom triggered actions (`datadogRum.addAction`) passed through unaltered
        if (event.action?.type === 'custom') {
          return true;
        }

        // Auto actions with names prefixed with `alloydd-` should be pre sent in dd rum. These should not cause PII leaks
        if (event?.action?.target?.name?.startsWith('alloydd-')) {
          // eslint-disable-next-line no-unsafe-optional-chaining
          const [, name] = event?.action?.target?.name.split('alloydd-');
          event.action.target.name = name;
        } else if (event?.action?.target?.name) {
          // remove target with potential PII from event
          event.action.target.name = '';
        }

        return true;
      },
    });
  }

  if (isProductionCell(environment)) {
    Sentry.init({
      dsn: sentryDsn,
      integrations: [browserTracingIntegration()],
      release: releaseVersion,
      tracesSampleRate: 0,
      environment,
      debug: !environment.includes('prod'),
      ignoreErrors: [
        'Non-Error',
        'The user aborted a request',
        'ResizeObserver loop',
        'Unknown option clicked',
      ],
      beforeSend(event, hint) {
        return sentryBeforeSendProcessing(event, hint);
      },
    });

    Sentry.addEventProcessor(event => {
      if (event?.exception?.values?.[0]?.mechanism) {
        const exception = event.exception.values[0];
        const mechanism = exception?.mechanism;

        if (
          mechanism?.type === 'instrument'
          && mechanism?.data?.target === 'EventTarget'
          && exception?.value?.match(
            /Cannot read|is not a function|is undefined/i,
          )
          && exception?.type === TypeError
        ) {
          event.tags.user_action = 'event';
        } else if (event.contexts?.trace?.op === 'pageload') {
          event.tags.user_action = 'visit_page';
        }

        if (
          mechanism.handled === false
          || mechanism.type === 'instrument'
          || !event?.tags?.enriched
        ) {
          const enrichmentData = enrichUnhandledExceptionData(event);

          event.tags = { ...event.tags, ...enrichmentData.tags };
          event.level = enrichmentData.level;
        }
      }

      return event;
    });
  }
};

export const configureObservabilitySuiteContexts = ({
  agent,
  customer,
  environment,
  featureFlags = {},
}) => {
  if (agent) {
    const {
      firstName,
      lastName,
      email,
      isAgent,
      isEmployee,
      ...agentUserData
    } = convertToCamel(agent);
    // `customer` is optional: employees mid-customer-switch have none
    const { organizationNameSlug } = convertToCamel(customer || {});
    const {
      datadogRecordAllSessions = false,
      datadogFeRumQaEnabled = false,
    } = convertToCamel(featureFlags);

    if (datadogFeRumQaEnabled && deferredNonProdRumConfig) {
      initializeObservabilitySuite({
        ...deferredNonProdRumConfig,
        environment,
        forceRum: true,
      });
      deferredNonProdRumConfig = null;
      hasStartedNonProdRum = true;
    }

    const agentCustomerOrgSlug = isEmployee
      ? organizationNameSlug
      : agentUserData?.customerAgents?.[0]?.organizationNameSlug;
    const agentFullName = `${firstName}${lastName ? ` ${lastName}` : ''}${isEmployee ? ' (Alloy)' : ''}`;

    if (isProductionCell(environment)) {
      Sentry.setUser({
        email,
        organization_name_slug: agentCustomerOrgSlug,
        isAlloyAdmin: isEmployee,
      });

      let releaseVersion = '';
      if (process.env.APP_VERSION) {
        releaseVersion = process.env.APP_VERSION;
      }

      // datadog cross integration purposes
      Sentry.setTag('organization_name_slug', agentCustomerOrgSlug);
      Sentry.setTag('env', environment);
      Sentry.setTag('version', releaseVersion);
      Sentry.setTag('isAlloyAdmin', isEmployee);
    }

    if (isProductionCell(environment) || hasStartedNonProdRum) {
      datadogRum.setUser({
        email,
        agentFullName,
        agentCustomerOrgSlug,
        isAlloyAdmin: isEmployee,
      });

      if (datadogRecordAllSessions) {
        // force in case we lost the sampling lottery earlier
        datadogRum.startSessionReplayRecording({ force: true });
      }
    }
  }
};

/**
 * Boots Intercom if the dashboard template rendered its snippet.
 *
 * The template renders the Intercom snippet only when INTERCOM_APP_ID is configured, so a
 * missing `window.Intercom` means this environment isn't set up for it -- nothing to boot.
 *
 * `userJwt` identifies the contact as a User rather than a Lead.
 * `connectorJwt` is the Alloy-signed Fin Data Connector credential, when minted.
 * `connectorJwtTokenKey` key of connectorJwt
 */
export const initializeSupportWidget = ({
  connectorJwt,
  connectorJwtTokenKey,
  userJwt,
} = {}) => {
  configureIntercom({
    connectorJwtTokenKey,
    onSuppressed: reason => {
      if (reason === 'rate-limited') datadogRum.addAction('intercom_update_rate_limited');
    },
  });

  if (!isIntercomAvailable()) return;

  bootIntercom(userJwt ? { intercom_user_jwt: userJwt } : {});
  if (connectorJwt) setAuthTokens(connectorJwt);
};

export function highlightSidebar() {
  let finished = false;

  function makeLinkActive($link) {
    const linkUrl = $link.find('a').attr('href');
    const reviewPage = /\/entities\/[PBE]-[a-zA-Z0-9]{12,}\/applications\/[a-zA-Z0-9]{32,}\/review\//;
    const isReviewQueue = linkUrl
      && linkUrl.includes('queues')
      && window.location.pathname.match(reviewPage);

    if (isReviewQueue || window.location.pathname.indexOf(linkUrl) > -1) {
      $link.addClass('active');
      finished = true;
    }
  }

  // Set sidebar tab to be active on page load
  if ($('li.queues-link')) makeLinkActive($('li.queues-link'));
  const $sidebarLinks = $(
    'nav#sidebar > ul.components > li:not(.expanded-toggle)',
  );
  $sidebarLinks.each(linkIdx => {
    if (!finished) makeLinkActive($sidebarLinks.eq(linkIdx));
  });
}

function convertMarkdown() {
  $('.convert-markdown').each((index, el) => {
    const $el = $(el);
    $el.html(marked($el.text()));
  });
}

function checkTimezone() {
  const sessionTzOffset = +document.getElementById('current_tz_offset').value;
  const browserTzOffset = new Date().getTimezoneOffset() * -1;
  const guessedTz = Moment.tz.guess();
  if (sessionTzOffset !== browserTzOffset) {
    // If the session timezone if different from the current browser timezone, update
    // the session timezone via the session data
    ajaxFetch(getRoute('sessionData'), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tz_offset: browserTzOffset, tz: guessedTz }),
    });
  }
}

function selectCellBaseAppUrlForTld(cellBaseAppUrls) {
  const mapping = {};
  for (const url of cellBaseAppUrls) {
    const tld = url.split('.').slice(-1)[0];
    mapping[tld] = url;
  }

  const currentTld = window.location.origin.split('.').slice(-1)[0];
  if (currentTld in mapping) {
    return mapping[currentTld];
  }
  return cellBaseAppUrls[0];
}

export async function initializePage(isSPA = false) {
  const authState = await ajaxFetch(routes.authentication.state, {
    camelCase: true,
  });
  const {
    alloyEnvironment,
    cellBaseAppUrl,
    datadogRumAppId,
    datadogRumClientToken,
    sentryDsn,
    isAuthenticated,
  } = authState;

  initializeObservabilitySuite({
    environment: alloyEnvironment,
    sentryDsn,
    datadogRumAppId,
    datadogRumClientToken,
  });
  await initializeI18n();

  /**
   * Background: devops has to manually update the google console with newly created customer vanity
   * urls (e.g organization-slug.app.alloy.com) to support successful google auth flow
   * This section of code eliminates the manual process by redirecting to the vanity url's true canonical
   * domain. e.g organiazation-slug.app.alloy.com may map to alloy-us.app.alloy.com.
   */
  const cellBaseAppUrls = cellBaseAppUrl ? cellBaseAppUrl.split(',') : [];
  const onVanityDomain = cellBaseAppUrls?.length
    ? !cellBaseAppUrls.includes(window.location.origin)
    : false;

  const authRoutes = [
    '/mfa/',
    '/password_reset',
    '/reset-password',
    '/password_change',
    '/verify_email',
    '/verify-email',
    '/verify_customer_email',
    '/verify-customer-email',
    '/invitation',
  ];
  const isUserOnAuthRoute = authRoutes.some(path => window.location.pathname.includes(path),
  );

  const isNeitherOnAuthOrOnAuthRoute = !isAuthenticated && !isUserOnAuthRoute;
  const redirectToLoginOnVanity = window.location.pathname.includes('/login') || isNeitherOnAuthOrOnAuthRoute;
  const isVanityUrlLogin = onVanityDomain && redirectToLoginOnVanity;
  const hasVanityRedirectParam = new URLSearchParams(window.location.search).get('vanityDomainRedirect')
    === 'true';

  if (isVanityUrlLogin && !hasVanityRedirectParam) {
    const encodeVanityUrl = `vanity_url=${encodeURIComponent(window.location.origin)}`;
    const selectedCellBaseAppUrl = selectCellBaseAppUrlForTld(cellBaseAppUrls);
    window.location.replace(
      `${selectedCellBaseAppUrl}/v3/dashboard/login?${encodeVanityUrl}`,
    );
  }

  const $sideNav = $('nav#sidebar');
  const $collapsers = $('div.container-fluid, ul#messages');

  const expandCollapseSidebar = expanded => {
    if (expanded) {
      $sideNav.addClass('expanded');
      $collapsers.removeClass('expanded');
    } else {
      $sideNav.removeClass('expanded');
      $collapsers.addClass('expanded');
    }
  };

  const isSidebarExpanded = localStorage.getItem('sidebarExpanded');
  expandCollapseSidebar(JSON.parse(isSidebarExpanded));

  function toggleSidebar() {
    const updatedIsSidebarExpanded = localStorage.getItem('sidebarExpanded');
    const expanded = JSON.parse(updatedIsSidebarExpanded);
    function updateSession(sidebarExpanded) {
      localStorage.setItem('sidebarExpanded', sidebarExpanded);
    }
    expandCollapseSidebar(!expanded);
    updateSession(!expanded);
  }

  if (!isSPA) {
    // fetch user data & initialize user session tracking
    const data = await ajaxFetch(routes.v3.userContext);
    initializeSupportWidget({
      connectorJwt: get(data, 'intercom.connector_jwt'),
      connectorJwtTokenKey: get(data, 'intercom.connector_jwt_token_key'),
      userJwt: get(data, 'intercom.user_jwt'),
    });
    const agentData = get(data, 'agent');
    const customerData = get(data, 'customer');
    const customerAccessRecord = get(
      get(data, 'admin_customer_access'),
      get(customerData, 'organization_name_slug'),
    );
    const adminPermissions = get(data, 'admin_permissions');
    const featureFlags = get(data, 'feature_flags');

    if (agentData && customerData) {
      configureObservabilitySuiteContexts({
        agent: agentData,
        customer: customerData,
        environment: alloyEnvironment,
        featureFlags,
      });
    }

    $('#sidebar #sidebar-support-form-cta').on('click', async () => {
      // Legacy non-SPA pages fetch user context once at boot and close over it. Re-fetch on
      // open so connector/user JWTs are not stale after a long-lived page (passenger tokens).
      let connectorJwt = get(data, 'intercom.connector_jwt');
      let userJwt = get(data, 'intercom.user_jwt');
      try {
        const fresh = await ajaxFetch(routes.v3.userContext);
        connectorJwt = get(fresh, 'intercom.connector_jwt');
        userJwt = get(fresh, 'intercom.user_jwt');
      } catch {
        // Fall back to boot-time tokens so Support still opens if the refetch fails.
      }

      openSupport({
        connectorJwt,
        userJwt,
        onRateLimited: () => flashMessages([
          'Something went wrong loading support. Please reload the page and try again.',
        ]),
      });
    });

    if (agentData.is_employee) {
      $('#customer-view-page-notice').css('display', 'flex');
      $('#customer-view-page-notice').children().css('display', 'flex');
      $('#customer-view-page-notice')
        .children()
        .css('justify-content', 'center');
      $('#org-name-env-text').text(
        `${kebabToCaps(alloyEnvironment)} - ${customerData.organization_name}`,
      );
      if (
        customerAccessRecord
        && customerAccessRecord.expired_at
        && !adminPermissions['READ:Customers:null']
      ) {
        $('#temp-access-expiration-timestamp').text(
          moment(customerAccessRecord.expired_at).format('M/D/YY h:mmA'),
        );
      } else {
        $('#temp-access-expiration-icon').css('display', 'none');
        $('#temp-access-expiration-timestamp').css('display', 'none');
      }
    }
    flashMessages();
    highlightSidebar();
    convertMarkdown();
    checkTimezone();

    // Instantiate top navigation bar dropdowns and tooltips
    $('nav#topbar button.dropdown-button').dropdown();
    $('nav#topbar a[data-toggle="tooltip"]').tooltip();

    $('li.expanded-toggle a').on('click', toggleSidebar);
    if ($(window).width() <= 767 && $('nav#sidebar').height() === 300) {
      toggleSidebar();
    }

    // eslint-disable-next-line
    const sessionSec = new SessionSecurity({
      pingUrl: $('input[name=session-security-ping-url]').val(),
      warnAfter: $('input[name=session-security-warn-after]').val(),
      expireAfter: $('input[name=session-security-expire-after]').val(),
      confirmFormDiscard: 'You have unsaved changes in a form of this page.',
    });
  }

  return { ...authState, isVanityUrlLogin };
}

export function initializeBasePage() {
  flashMessages();
  // Not awaited: initialisation must not block on a support widget. The loader handles an
  // unreachable endpoint itself; anything else is a bug and should surface.
  loadPublicSupportWidget();
}
